// SimpleFIN Bridge REST client (browser-direct — no proxy).
//
// CORS verified working against beta-bridge.simplefin.org from
// personal-finance-app-cj4.pages.dev: preflight 200 with Allow-Origin
// echo, allow-headers authorization, allow-credentials true. So all
// HTTP happens directly from the user's browser — nothing transits a
// server we operate.
//
// Flow (mirrors the desktop's src-tauri/src/simplefin.rs):
//   1. User signs up at beta-bridge.simplefin.org, links banks, copies a
//      single-use base64 setup token.
//   2. We POST the decoded claim URL → SimpleFIN returns a permanent
//      access_url. Setup token is now consumed; persist the access_url
//      to IndexedDB.
//   3. All future syncs: GET <access_url>/accounts?start-date=<ts>&end-date=<ts>.
//      The userinfo segment of access_url carries HTTP Basic credentials —
//      we extract them and pass them via the Authorization header (browsers
//      do not forward userinfo in fetch() unlike curl).
//
// SimpleFIN docs: https://www.simplefin.org/protocol.html

// ── decode ─────────────────────────────────────────────────────────

/** Decode a base64-encoded setup_token to its claim URL. Network is NOT
 *  touched here — separated so it can be unit-tested + so we can preview
 *  to the user before firing the consumes-it POST. */
export function decodeSetupToken(token: string): string {
  const trimmed = (token ?? "").trim();
  if (!trimmed) throw new Error("Setup token is empty");
  let decoded: string;
  try {
    // atob handles base64 in browsers + jsdom/Node. Allow padded or
    // unpadded inputs by re-padding if length isn't a multiple of 4.
    const padded = trimmed + "=".repeat((4 - (trimmed.length % 4)) % 4);
    decoded = atob(padded);
  } catch {
    throw new Error("Setup token is not valid base64");
  }
  const claimUrl = decoded.trim();
  try {
    new URL(claimUrl);
  } catch {
    throw new Error("Decoded setup token is not a valid URL");
  }
  return claimUrl;
}

// ── claim ──────────────────────────────────────────────────────────

/** Exchange a setup token for a permanent access_url. The token is
 *  single-use — calling this twice with the same token will fail the
 *  second time. */
export async function exchangeSetupToken(token: string): Promise<string> {
  const claimUrl = decodeSetupToken(token);
  const resp = await fetch(claimUrl, { method: "POST" });
  if (!resp.ok) {
    throw new Error(
      `SimpleFIN claim failed (HTTP ${resp.status}). Setup tokens are single-use — if you've already redeemed this one, generate a new one from your SimpleFIN dashboard.`,
    );
  }
  const accessUrl = (await resp.text()).trim();
  try {
    new URL(accessUrl);
  } catch {
    throw new Error("SimpleFIN response is not a valid access URL.");
  }
  return accessUrl;
}

// ── fetch ──────────────────────────────────────────────────────────

/** Strip userinfo from an access URL and return both pieces.
 *  Browsers refuse to forward URLs with embedded userinfo through
 *  fetch() (a deliberate XSS-resistance measure), so we pull the
 *  credentials out and pass them via Authorization: Basic ... instead. */
export function splitAccessUrl(accessUrl: string): { url: URL; authHeader: string | null } {
  const u = new URL(accessUrl);
  const user = u.username;
  const pass = u.password;
  let authHeader: string | null = null;
  if (user || pass) {
    // btoa wants a Latin-1 string; user/pass for SimpleFIN are ASCII.
    authHeader = "Basic " + btoa(`${decodeURIComponent(user)}:${decodeURIComponent(pass)}`);
    u.username = "";
    u.password = "";
  }
  return { url: u, authHeader };
}

export interface SimpleFinOrg {
  domain?: string;
  name?: string;
  "sfin-url"?: string;
}

export interface SimpleFinTx {
  id: string;
  posted: number;
  amount: string;
  description: string;
  payee?: string;
  memo?: string;
  pending?: boolean;
}

export interface SimpleFinAccount {
  id: string;
  name: string;
  currency: string;
  balance?: string;
  org: SimpleFinOrg;
  transactions?: SimpleFinTx[];
}

export interface SimpleFinResponse {
  errors?: string[];
  accounts: SimpleFinAccount[];
}

/** Pull accounts + transactions from the access_url. Default window is
 *  90 days back through end-of-tomorrow (matches the desktop default).
 *  Returns the raw SimpleFIN JSON body — the caller hands it to
 *  loadSimplefinJson for the transform-to-Transaction pass. */
export async function fetchAccounts(
  accessUrl: string,
  options: { startDate?: Date; endDate?: Date } = {},
): Promise<SimpleFinResponse> {
  const { url, authHeader } = splitAccessUrl(accessUrl);

  // Append /accounts to the path. Trailing slash on access_url shouldn't
  // change the result but we normalise just in case.
  const pathBase = url.pathname.endsWith("/") ? url.pathname.slice(0, -1) : url.pathname;
  url.pathname = `${pathBase}/accounts`;

  const now = new Date();
  const start = options.startDate ?? new Date(now.getTime() - 90 * 86400 * 1000);
  const end = options.endDate ?? new Date(now.getTime() + 86400 * 1000);
  url.searchParams.set("start-date", String(Math.floor(start.getTime() / 1000)));
  url.searchParams.set("end-date", String(Math.floor(end.getTime() / 1000)));

  const headers: HeadersInit = { Accept: "application/json" };
  if (authHeader) (headers as Record<string, string>).Authorization = authHeader;

  const resp = await fetch(url.toString(), { method: "GET", headers });
  if (!resp.ok) {
    throw new Error(`SimpleFIN /accounts fetch failed (HTTP ${resp.status}).`);
  }
  const body = (await resp.json()) as SimpleFinResponse;
  if (!Array.isArray(body.accounts)) {
    throw new Error("SimpleFIN response missing 'accounts' array.");
  }
  return body;
}
